// The yearbook (scenario H9's league half, PLAN-2026-10-31-the-yearbook): the
// league's season told once its champion is crowned — the champion on the
// cover, the class (every team and the one thing it is remembered for), the
// records set, the games to remember, the moves — ending in the class photo
// to share. The words are the server's (`YearbookView`); this screen only
// lays them out.
//
// A PAGE, NOT A STORY: the class is every team, a list rather than a card,
// so it reads down one region on a phone and in columns wider (§4). A story
// and a social object like the recap, it keeps its numbers at every level.
// Drawn through the kit (LOOKS.md Part 7).

import { useNavigate } from 'react-router-dom';
import type { YearbookView } from '@fsp/domain';
import { useLeagueBase, useYearbook } from '../data/hooks';
import { TeamMark } from '../kit';
import { Panel } from '../kit/Panel';
import { TeamFigure, teamFace } from '../kit/TeamFigure';
import { Button, Caption, Cover, Line, ListRow, RowName, RowSub, Text, Title } from '../kit/ui';
import { DashboardScreen, type Widget } from '../templates/DashboardScreen';
import { useTier } from '../shell/useTier';
import { AwardIcon, Cup, SharePanel } from './shared/AwardKit';
import { FromLeague } from './shared/FromLeague';
import { ordinalOf } from './shared/rank';
import '../styles/screens/waivers.css';
import '../styles/screens/yearbook.css';

type Book = YearbookView;

const PLACE: Record<Book['teams'][number]['how'], string | null> = {
  champion: 'Champion',
  'runner-up': 'Runner-up',
  playoffs: 'Playoffs',
  missed: null,
  finished: null,
};

/** "Alpha’s", and "Jen’s Jets’" for a name that ends in an s, as the yearbook's own lines say it. */
function possessive(name: string): string {
  return name.endsWith('s') || name.endsWith('S') ? `${name}’` : `${name}’s`;
}

export function YearbookScreen() {
  return <FromLeague snapshot={useYearbook()}>{(book) => <YearbookBody book={book} />}</FromLeague>;
}

function YearbookBody({ book }: { book: Book }) {
  const base = useLeagueBase();
  const tier = useTier();
  const navigate = useNavigate();
  const title = `The ${book.season} yearbook`;

  const header = (
    <div className="fsp-waivers-head">
      <div className="fsp-waivers-head-body">
        <Title>{title}</Title>
        <div className="fsp-sub fsp-ui-wrap-text">{book.league.name}</div>
      </div>
    </div>
  );

  if (!book.over || book.champion === null) {
    return (
      <div className="fsp-screen">
        <div className="fsp-pinned">{header}</div>
        <div className="fsp-empty">
          The {book.season} yearbook is written once the champion is crowned.
        </div>
      </div>
    );
  }
  const champ = book.champion;
  const seasonOf = (team: Book['teams'][number]['team']) =>
    team.isMine ? `${base}/wrapped` : `${base}/wrapped?team=${encodeURIComponent(team.id)}`;

  const cover = (
    <Panel title="The champion">
      <div className="fsp-yearbook-cover">
        <div className="fsp-yearbook-cover-art">
          <Cup
            primary={champ.team.colors.primary}
            secondary={champ.team.colors.secondary}
            size={84}
          />
          {teamFace(champ.team) !== null ? (
            <TeamFigure team={champ.team} mood="pleased" height={104} />
          ) : (
            <TeamMark team={champ.team} size={56} />
          )}
        </div>
        <Caption>{champ.title}</Caption>
        <div className="fsp-disp fsp-yearbook-champion">{champ.team.name}</div>
        <Text tone="secondary">{champ.team.ownerName}</Text>
        <Text>{champ.line}</Text>
        <Line gap={2} wrap>
          {champ.matchupId ? (
            <Button variant="primary" icon="trophy" to={`${base}/matchup/${champ.matchupId}`}>
              Open the final
            </Button>
          ) : null}
          <Button to={`${base}/awards?tab=room&team=${encodeURIComponent(champ.team.id)}`}>
            Trophy room
          </Button>
        </Line>
      </div>
    </Panel>
  );

  const theClass = (
    <Panel title={`The class of ${book.season}`}>
      <div className="fsp-yearbook-class">
        {book.teams.map((t) => {
          const place = [
            PLACE[t.how],
            [t.record, t.rank === null ? null : ordinalOf(t.rank)].filter(Boolean).join(', '),
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <ListRow
              key={t.team.id}
              tall
              leadSize="lg"
              lead={<TeamMark team={t.team} size={36} />}
              cover={t.team.isMine ? 'Open your season' : `Open ${possessive(t.team.name)} season`}
              onClick={() => navigate(seasonOf(t.team))}
            >
              <RowName>{t.team.name}</RowName>
              <RowSub>{place}</RowSub>
              <div className="fsp-yearbook-tag" data-tone={t.tone}>
                {t.icon ? (
                  <span className="fsp-award-icon fsp-chat-card-glyph" data-tone={t.tone}>
                    <AwardIcon icon={t.icon} size={12} />
                  </span>
                ) : null}
                <span className="fsp-yearbook-tag-name">{t.tag}</span>
              </div>
              <RowSub wrap>{t.line}</RowSub>
            </ListRow>
          );
        })}
      </div>
    </Panel>
  );

  const records = (
    <Panel
      title="Records set"
      meta={
        <Button variant="quiet" size="xs" to={`${base}/history`}>
          Record book
        </Button>
      }
    >
      <div className="fsp-yearbook-note">
        <Text tone="secondary">{book.records.line}</Text>
      </div>
      {book.records.set.map((r) => (
        <ListRow
          key={r.id}
          tall
          lead={<TeamMark team={r.team} size={28} />}
          trail={<span className="fsp-num fsp-yearbook-value">{r.value}</span>}
          cover={`Open ${possessive(r.team.name)} trophy room`}
          onClick={() => navigate(`${base}/awards?tab=room&team=${encodeURIComponent(r.team.id)}`)}
        >
          <RowName>{r.name}</RowName>
          <RowSub wrap>{r.line}</RowSub>
        </ListRow>
      ))}
    </Panel>
  );

  const games =
    book.games.length > 0 ? (
      <Panel title="Games to remember">
        <div className="fsp-yearbook-games">
          {book.games.map((g) => (
            <div key={g.id} className="fsp-yearbook-game fsp-row-clickable fsp-cover-host">
              <Cover
                label={`Open ${g.title.toLowerCase()}`}
                to={`${base}/matchup/${g.matchupId}`}
              />
              <Line spread>
                <Caption>{g.title}</Caption>
                {g.value ? <span className="fsp-num fsp-yearbook-value">{g.value}</span> : null}
              </Line>
              {[g.home, g.away].map((side) => (
                <div
                  key={side.team.id}
                  className="fsp-yearbook-side"
                  data-won={side.won || undefined}
                >
                  <TeamMark team={side.team} size={20} />
                  <span className="fsp-yearbook-side-name">{side.team.name}</span>
                  <span className="fsp-num">{side.score.toFixed(1)}</span>
                </div>
              ))}
              <RowSub wrap>{g.line}</RowSub>
            </div>
          ))}
        </div>
      </Panel>
    ) : null;

  const moves = (
    <Panel title="On the wire">
      <div className="fsp-yearbook-note">
        <Text tone="secondary">{book.moves.line}</Text>
      </div>
      {book.moves.items.map((m) => (
        <ListRow
          key={m.id}
          tall
          lead={<TeamMark team={m.team} size={28} />}
          trail={<span className="fsp-num fsp-yearbook-value">{m.value}</span>}
        >
          <RowName>{m.title}</RowName>
          <RowSub wrap>{m.line}</RowSub>
        </ListRow>
      ))}
    </Panel>
  );

  const share = book.share ? (
    <Panel title="The class photo">
      <div className="fsp-yearbook-share">
        <SharePanel
          input={{
            ...book.share,
            footer: `${book.league.name} · ${book.season}`,
            team: champ.team,
          }}
          photo={{
            kicker: book.share.kicker,
            title: book.league.name,
            champion: { name: champ.team.name, title: champ.title, team: champ.team },
            teams: book.teams.map((t) => ({
              name: t.team.name,
              tag: t.tag,
              tone: t.tone,
              team: t.team,
            })),
            footer: `${book.league.name} · ${book.season}`,
          }}
          text={`${book.league.name}, the ${book.season} yearbook. ${book.share.line}`}
        />
      </div>
    </Panel>
  ) : null;

  // ONE ORDER ON A PHONE, COLUMNS WIDER (§4): a tablet's two keep the cover,
  // the records and the games beside the class; a desktop's three give the
  // class its own.
  const wide = tier === 'desktop' || tier === 'large';
  const widgets: Widget[] = [
    { id: 'cover', column: 1, content: cover },
    { id: 'class', column: 2, content: theClass },
    { id: 'records', column: 1, content: records },
    ...(games ? [{ id: 'games', column: wide ? (3 as const) : (1 as const), content: games }] : []),
    { id: 'moves', column: wide ? 3 : 2, content: moves },
    ...(share ? [{ id: 'share', column: wide ? (3 as const) : (2 as const), content: share }] : []),
  ];

  return (
    <div className="fsp-screen">
      <div className="fsp-pinned">{header}</div>
      <DashboardScreen widgets={widgets} />
    </div>
  );
}
